// A game's developers and publishers, as the site's company pages
// (/developer/<slug>, /publisher/<slug>) group them. Shared by the site, the
// API (workers/web), which finds a page's games by slug, and the crawler
// (workers/crawler), which writes each game's companies into app_companies as
// it fetches its store details. One copy, so a link the site makes is always
// one the API can find.

import type { StoreInfo } from "./api.ts";

// The roles a company has on a game, as store_info lists them (its key) and
// as the URL and app_companies name them, with how pages say them.
// `other` is the role's counterpart, and `tab` its name in the tabs between a
// company's two pages.
export const COMPANY_ROLES = {
  developer: {
    key: "developers", label: "Developer", verb: "developed", tab: "Developed", other: "publisher"
  },
  publisher: {
    key: "publishers", label: "Publisher", verb: "published", tab: "Published", other: "developer"
  }
} as const satisfies Record<
  string,
  {
    // The StoreInfo list it's read from (storeCompanies).
    key: "developers" | "publishers";
    label: string;
    verb: string;
    tab: string;
    other: string;
  }
>;

// A company's role on a game: "developer" or "publisher".
export type CompanyRole = keyof typeof COMPANY_ROLES;

export const isCompanyRole = (role: string): role is CompanyRole =>
  Object.hasOwn(COMPANY_ROLES, role);

// The roles, in COMPANY_ROLES order.
export const COMPANY_ROLE_LIST: readonly CompanyRole[] = Object.keys(COMPANY_ROLES).filter(isCompanyRole);

// A game's company as app_companies keeps it.
export interface StoreCompany {
  role: CompanyRole;
  slug: string;
  name: string;
}

// Words that end company names without telling them apart: "CAPCOM Co., Ltd."
// and "Capcom" are one company, "Jagex Ltd" is "jagex".
const SUFFIXES = new Set([
  "inc", "ltd", "llc", "co", "corp", "corporation", "limited", "gmbh", "pte",
  "plc", "sa", "srl", "ab", "oy", "bv", "kk", "pty"
]);

// Legal suffixes spelt with a slash or dots ("IO Interactive A/S", "Foo
// S.A."), which would otherwise read as the words "a" and "s".
const LETTER_SUFFIX = /,?\s+(?:a\/s|s\.\s?a\.?)\s*$/i;

// A store entry that's a port of the game to another platform, which Steam
// lists as a developer or publisher of its own ("Feral Interactive (Mac)",
// "Aspyr (Linux)"). Left out: on a game page they'd say "more from" a
// porting house rather than whoever made the game.
const PORT = /\((?:mac|macos|os x|linux|steamos)\)\s*$/i;

// A company's name as its page's URL spells it: letters and digits in
// lowercase runs joined by "-", accents dropped, without a region note in
// brackets ("FromSoftware, Inc. (Japan)") or a trailing legal suffix, so the
// names one company goes by share a page. Up to 60 code points; "" when
// nothing's left, which gets no page.
export function companySlug(name: string): string {
  const words =
    name
      .replace(/\([^)]*\)\s*$/, "")
      .replace(LETTER_SUFFIX, "")
      // As searchWords does: "Dad's" is one word, not "dad" and "s".
      .replace(/['’]/g, "")
      .normalize("NFKD")
      .replace(/\p{M}/gu, "")
      .toLowerCase()
      .match(/[\p{L}\p{N}]+/gu) ?? [];

  while (words.length > 1 && SUFFIXES.has(words.at(-1) ?? "")) words.pop();

  // By code point, so a letter outside the BMP is never cut in half.
  return Array.from(words.join("-")).slice(0, 60).join("").replace(/-+$/, "");
}

// A company page's role and slug from a path's two parts after its "/"
// (`role` and `raw`, the slug still percent-encoded), as { role, slug }, or
// null for a role that isn't one or a slug that can't be decoded. The site
// and the API both read their paths with it, so one never takes a slug the
// other refuses.
export function companyParts(
  role: string,
  raw: string
): { role: CompanyRole; slug: string } | null {
  if (!isCompanyRole(role) || raw.length > 200) return null;
  try {
    return { role, slug: decodeURIComponent(raw) };
  } catch {
    return null;
  }
}

// Whether `slug` is one companySlug could have written, so a request for any
// other is answered without a lookup.
export const isCompanySlug = (slug: string): boolean =>
  Array.from(slug).length <= 60 &&
  slug === slug.toLowerCase() &&
  /^[\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)*$/u.test(slug);

// A game's companies from its store_info (`store`), as app_companies keeps
// them: [{ role, slug, name }], each role's in the store's order, ports and
// names with no slug left out, and a slug once per role.
export function storeCompanies(store: StoreInfo | null | undefined): StoreCompany[] {
  const companies: StoreCompany[] = [];

  for (const role of COMPANY_ROLE_LIST) {
    const { key } = COMPANY_ROLES[role];
    const seen = new Set<string>();
    for (const name of store?.[key] ?? []) {
      const slug = PORT.test(name) ? "" : companySlug(name);
      if (!slug || seen.has(slug)) continue;
      seen.add(slug);
      companies.push({ role, slug, name });
    }
  }

  return companies;
}
